import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { getCountries } from "countrycitystatejson";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

interface ManufacturerFilterProps {
  currentFilters?: {
    country: string;
    search: string;
    isActive: string;
  };
  onApplyFilters: (filters: any) => void;
  onResetFilters: () => void;
}

export const ManufacturerFilter: React.FC<ManufacturerFilterProps> = ({
  currentFilters = { country: "all", search: "", isActive: "" },
  onApplyFilters,
  onResetFilters,
}) => {
  const countryData = getCountries();
  const statusData = [
    {
      value: 1,
      label: "Active",
    },
    {
      value: 0,
      label: "Inactive",
    },
  ];
  // Local state for temporary filter values
  const [tempFilters, setTempFilters] = useState<{
    country: string;
    isActive: string;
  }>({
    country: currentFilters?.country || "all",
    isActive: currentFilters?.isActive || "",
  });

  // Initialize form with current filter values
  const form = useForm({
    defaultValues: {
      country: tempFilters.country,
      isActive: tempFilters.isActive,
    },
  });

  // Update temp filters when current filters change (e.g., from URL)
  useEffect(() => {
    const newTempFilters = {
      country: currentFilters?.country || "all",
      isActive: currentFilters?.isActive || "",
    };

    setTempFilters(newTempFilters);

    // Update form values
    form.setValue("country", newTempFilters.country);
  }, [currentFilters, form]);

  // Update form when tempFilters change
  useEffect(() => {
    form.setValue("country", tempFilters.country);
  }, [tempFilters, form]);

  const handleCountryChange = (value: string) => {
    setTempFilters((prev) => ({
      ...prev,
      country: value,
    }));
  };

  const handleStatusChange = (value: string) => {
    setTempFilters((prev) => ({
      ...prev,
      isActive: value,
    }));
  };

  const handleApplyFilters = () => {
    const filtersToApply: any = {};

    if (tempFilters.country && tempFilters.country !== "all") {
      filtersToApply.country = tempFilters.country;
    }

    if (tempFilters.isActive && tempFilters.isActive !== "") {
      filtersToApply.isActive = tempFilters.isActive;
    }

    onApplyFilters(filtersToApply);
  };

  const handleResetFilters = () => {
    setTempFilters({
      country: "all",
      isActive: "",
    });
    onResetFilters();
  };

  const handleSubmit = (data: any) => {
    handleApplyFilters();
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Country</Label>
          <Controller
            name="country"
            control={form.control}
            render={({ field }) => (
              <Select
                value={tempFilters.country}
                onValueChange={(value) => {
                  field.onChange(value);
                  handleCountryChange(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">All Countries</SelectItem>
                    {countryData?.map((country) => (
                      <SelectItem key={country.name} value={country.name}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div className="space-y-2">
          <Label>Status</Label>
          <Controller
            name="isActive"
            control={form.control}
            render={({ field }) => (
              <Select
                value={tempFilters.isActive}
                onValueChange={(value) => {
                  field.onChange(value);
                  handleStatusChange(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">All Status</SelectItem>
                    {statusData?.map((status) => (
                      <SelectItem
                        key={status.value.toString()}
                        value={status.value.toString()}
                      >
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="col-span-1 md:col-span-2 lg:col-span-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleResetFilters}>
            Reset Filters
          </Button>
          <Button type="submit">Apply Filters</Button>
        </div>
      </div>
    </form>
  );
};
